import { useCallback, useEffect, useState } from 'react';

/**
 * Local cooldown hook for actions that should be blocked for a period after
 * the current user triggers them in the UI.
 *
 * Use this when the cooldown starts "now" on the client, for example after a
 * successful resend request. If the source of truth is a timestamp from the
 * backend, prefer `useCountdown` instead.
 */
const useCooldown = () => {
  const [cooldownExpiresAt, setCooldownExpiresAt] = useState<number | null>(
    null,
  );
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    if (cooldownExpiresAt === null) {
      setRemainingMs(0);
      return;
    }

    let intervalId = 0;

    const updateRemainingMs = () => {
      const nextRemainingMs = Math.max(cooldownExpiresAt - Date.now(), 0);
      setRemainingMs(nextRemainingMs);

      if (nextRemainingMs === 0) {
        setCooldownExpiresAt(null);
        window.clearInterval(intervalId);
      }
    };

    updateRemainingMs();
    intervalId = window.setInterval(updateRemainingMs, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [cooldownExpiresAt]);

  const startCooldown = useCallback((durationInMs: number) => {
    if (durationInMs <= 0) {
      setCooldownExpiresAt(null);
      setRemainingMs(0);
      return;
    }

    setCooldownExpiresAt(Date.now() + durationInMs);
    setRemainingMs(durationInMs);
  }, []);

  return {
    isActive: remainingMs > 0,
    remainingMs,
    startCooldown,
  };
};

export default useCooldown;
